import { useEffect, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useSessione } from '../../servizi/sessione.tsx'
import { Intestazione, Vuoto } from '../../componenti/Pezzi.tsx'
import { Campo, Riquadro } from '../../componenti/base.tsx'
import { IconaAvviso, IconaIndietro } from '../../componenti/Icone.tsx'
import { capitoliPer, cerca, GRUPPI } from '../../guida/indice.ts'
import { Figura, Testo, testoPiano } from './pezzi.tsx'

/**
 * Guida illustrata (decisione di Davide del 31/10/2026): un capitolo per funzione, con a che cosa
 * serve, i passi, le catture e gli avvisi che il gestionale mostra. I testi stanno in guida/capitoli/;
 * qui solo la loro impaginazione. Ognuno vede i capitoli e le parti che servono ai suoi incarichi,
 * con la stessa regola delle voci di menu.
 */
export function Guida() {
  const { puo } = useSessione()
  const [parametri, setParametri] = useSearchParams()
  const domanda = parametri.get('cerca') ?? ''
  const capitoli = useMemo(() => capitoliPer(puo), [puo])
  const risultati = useMemo(() => cerca(domanda, capitoli), [domanda, capitoli])

  // La domanda resta nell'indirizzo: tornando indietro da un capitolo si ritrovano i risultati.
  const cambiaDomanda = (v: string) => setParametri(v ? { cerca: v } : {}, { replace: true })

  return (
    <>
      <Intestazione
        titolo="Guida"
        sottotitolo="Come si usa il gestionale, un capitolo per funzione. Trovi i capitoli che servono con i tuoi incarichi."
        azioni={<Link className="bottone bottone-secondario" to="/novita">Novità</Link>}
      />
      <div className="guida-cerca">
        <Campo tipo="search" etichetta="Cerca nella guida" valore={domanda} cambia={cambiaDomanda}
               segnaposto="Per esempio: consensi, bozza, invito" />
      </div>

      {domanda.trim() ? (
        risultati.length ? (
          <ul className="guida-risultati" aria-label="Capitoli trovati">
            {risultati.map(({ capitolo, dove }) => (
              <li key={capitolo.id}>
                <Link to={`/guida/${capitolo.id}`}>{capitolo.titolo}</Link>
                {dove.length > 0 && <span className="piccolo tenue"> · ne parla: {dove.join(' · ')}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <Vuoto testo="Nessun capitolo contiene queste parole. Prova con un’altra parola, oppure guarda l’indice." />
        )
      ) : (
        GRUPPI.map((gruppo) => {
          const delGruppo = capitoli.filter((c) => c.gruppo === gruppo)
          if (!delGruppo.length) return null
          return (
            <section key={gruppo} className="guida-gruppo">
              <h2>{gruppo}</h2>
              <ul className="guida-indice">
                {delGruppo.map((c) => (
                  <li key={c.id}>
                    <Link to={`/guida/${c.id}`} className="guida-voce">
                      <span className="guida-voce-titolo">{c.titolo}</span>
                      <span className="guida-voce-testo">{testoPiano(c.aCheServe)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )
        })
      )}
    </>
  )
}

/** Un capitolo: a che cosa serve, le parti con i passi e le catture, gli avvisi, i rimandi. */
export function CapitoloGuida() {
  const { puo } = useSessione()
  const { capitolo: id } = useParams()
  const capitoli = useMemo(() => capitoliPer(puo), [puo])
  const posizione = capitoli.findIndex((c) => c.id === id)
  const c = capitoli[posizione]

  // Da un capitolo all'altro la pagina riparte dall'alto, non dal punto in cui si era arrivati.
  useEffect(() => { window.scrollTo(0, 0) }, [id])

  if (!c) {
    return (
      <>
        <Intestazione titolo="Guida" />
        <Vuoto testo="Questo capitolo non c’è, oppure non riguarda i tuoi incarichi."
               azione={<Link to="/guida">Torna all’indice della guida</Link>} />
      </>
    )
  }

  const prima = capitoli[posizione - 1]
  const dopo = capitoli[posizione + 1]
  // Solo i rimandi a capitoli che l'utente vede.
  const rimandi = (c.vediAnche ?? []).flatMap((v) => capitoli.filter((x) => x.id === v))

  return (
    <article className="guida-capitolo">
      <nav className="guida-briciole" aria-label="Percorso">
        <Link to="/guida"><IconaIndietro /> Guida</Link>
        <span className="tenue"> · {c.gruppo}</span>
      </nav>
      <Intestazione titolo={c.titolo} />

      <p className="guida-a-che-serve"><Testo t={c.aCheServe} /></p>

      {c.parti.map((p) => (
        <Riquadro key={p.titolo} titolo={p.titolo}>
          {p.testo?.map((t, i) => <p key={i} className="guida-paragrafo"><Testo t={t} /></p>)}
          {p.passi && (
            <ol className="guida-passi">
              {p.passi.map((t, i) => <li key={i}><Testo t={t} /></li>)}
            </ol>
          )}
          {p.immagine && <Figura immagine={p.immagine} />}
          {p.nota && <p className="guida-nota"><Testo t={p.nota} /></p>}
        </Riquadro>
      ))}

      {c.avvertenze.length > 0 && (
        <Riquadro titolo={<span className="titolo-con-icona"><IconaAvviso /> Avvisi ed errori comuni</span>}>
          <dl className="guida-avvertenze">
            {c.avvertenze.map((a) => (
              <div key={a.quando}>
                <dt><Testo t={a.quando} /></dt>
                <dd><Testo t={a.cosaFare} /></dd>
              </div>
            ))}
          </dl>
        </Riquadro>
      )}

      {rimandi.length > 0 && (
        <p className="guida-rimandi">
          Vedi anche:{' '}
          {rimandi.map((r, i) => (
            <span key={r.id}>{i > 0 && ' · '}<Link to={`/guida/${r.id}`}>{r.titolo}</Link></span>
          ))}
        </p>
      )}

      <nav className="guida-scorri" aria-label="Altri capitoli">
        {prima ? <Link to={`/guida/${prima.id}`}>← {prima.titolo}</Link> : <span />}
        {dopo ? <Link to={`/guida/${dopo.id}`}>{dopo.titolo} →</Link> : <span />}
      </nav>
    </article>
  )
}
